import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  approveExpertApi,
  certifyExpertApi,
  fetchExpert,
  fetchExperts,
  fetchPendingExpertRequests,
  recommendExpertApi,
  rejectExpertApi,
  unvalidateExpertApi,
  updateExpertApi,
  validateExpertApi,
} from "../services/expertsService";

export { isPendingExpertRequest } from "../services/expertsService";

const EXPERTS_QUERY_KEY = ["experts"];

export const useExperts = () =>
  useQuery({
    queryKey: EXPERTS_QUERY_KEY,
    queryFn: fetchExperts,
  });

/**
 * Applications awaiting the committee (validation) or an admin (approval).
 * Keyed under the experts prefix so every mutation below, which invalidates
 * that prefix, refreshes the sidebar badge and the list together.
 */
export const usePendingExpertRequests = (options = {}) =>
  useQuery({
    queryKey: [...EXPERTS_QUERY_KEY, "pending"],
    queryFn: fetchPendingExpertRequests,
    ...options,
  });

export const useExpert = (id, options = {}) =>
  useQuery({
    queryKey: [...EXPERTS_QUERY_KEY, id],
    queryFn: () => fetchExpert(id),
    enabled: Boolean(id),
    ...options,
  });

export const useValidateExpert = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => validateExpertApi(id, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: EXPERTS_QUERY_KEY });
      if (variables?.id) {
        queryClient.invalidateQueries({
          queryKey: [...EXPERTS_QUERY_KEY, variables.id],
        });
      }
      toast.success("Expert en validation", { autoClose: 3000 });
    },
    onError: (err) => {
      const msg = err?.message || "Expert non validé";
      toast.error(msg, { autoClose: 3000 });
    },
  });
};

export const useUpdateExpert = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => updateExpertApi(id, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: EXPERTS_QUERY_KEY });
      if (variables?.id) {
        queryClient.invalidateQueries({
          queryKey: [...EXPERTS_QUERY_KEY, variables.id],
        });
      }
      toast.success("Expert mis à jour", { autoClose: 3000 });
    },
    onError: (err) => {
      const msg = err?.message || "Erreur lors de la mise à jour";
      toast.error(msg, { autoClose: 3000 });
    },
  });
};

export const useUnvalidateExpert = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => unvalidateExpertApi(id, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: EXPERTS_QUERY_KEY });
      if (variables?.id) {
        queryClient.invalidateQueries({
          queryKey: [...EXPERTS_QUERY_KEY, variables.id],
        });
      }
      toast.error("Expert non validé", { autoClose: 3000 });
    },
    onError: (err) => {
      const msg = err?.message || "Erreur lors de la non-validation";
      toast.error(msg, { autoClose: 3000 });
    },
  });
};

export const useApproveExpert = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => approveExpertApi(id, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: EXPERTS_QUERY_KEY });
      if (variables?.id) {
        queryClient.invalidateQueries({
          queryKey: [...EXPERTS_QUERY_KEY, variables.id],
        });
      }
      toast.success("Expert approuvé avec succès", { autoClose: 3000 });
    },
    onError: (err) => {
      const msg = err?.message || "Expert non approuvé";
      toast.error(msg, { autoClose: 3000 });
    },
  });
};

export const useRejectExpert = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => rejectExpertApi(id, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: EXPERTS_QUERY_KEY });
      if (variables?.id) {
        queryClient.invalidateQueries({
          queryKey: [...EXPERTS_QUERY_KEY, variables.id],
        });
      }
      toast.error("Expert non approuvé", { autoClose: 3000 });
    },
    onError: (err) => {
      const msg = err?.message || "Erreur lors du rejet";
      toast.error(msg, { autoClose: 3000 });
    },
  });
};

export const useRecommendExpert = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => recommendExpertApi(id, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: EXPERTS_QUERY_KEY });
      if (variables?.id) {
        queryClient.invalidateQueries({
          queryKey: [...EXPERTS_QUERY_KEY, variables.id],
        });
      }
      toast.success("Recommandation envoyée", { autoClose: 3000 });
    },
    onError: (err) => {
      const msg = err?.message || "Erreur lors de la recommandation";
      toast.error(msg, { autoClose: 3000 });
    },
  });
};

export const useCertifyExpert = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => certifyExpertApi(id, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: EXPERTS_QUERY_KEY });
      if (variables?.id) {
        queryClient.invalidateQueries({
          queryKey: [...EXPERTS_QUERY_KEY, variables.id],
        });
      }
      toast.success("Expert certifié", { autoClose: 3000 });
    },
    onError: (err) => {
      const msg = err?.message || "Expert non certifié";
      toast.error(msg, { autoClose: 3000 });
    },
  });
};
